import { useState, useEffect, useCallback, useRef } from "react";
import { Mic, StopCircle, ArrowLeft, Loader2, Trash2, Pencil, Check, X, Sparkles, ShieldCheck, RotateCcw, Type, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useRecorder } from "@/hooks/useRecorder";
import {
  listFragments, addVoiceFragment, addTextFragment, updateFragment, deleteFragment, finalizeSession, errMsg,
} from "@/api";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const hora = (iso) => {
  try { return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

// Sesión de un paciente en varias notas. Cada nota (de voz o escrita) se guarda en el
// servidor en cuanto se hace, cifrada; al pulsar «Finalizar» se generan los dos
// documentos con todas. Si se sale sin finalizar, las notas siguen ahí al volver.
export const SessionView = ({ patient, appointmentId = null, onBack, onFinalized }) => {
  const { recording, seconds, blob, start, stop, reset } = useRecorder();
  const [fragments, setFragments] = useState([]);
  const [expiresHours, setExpiresHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [text, setText] = useState("");
  const [addingText, setAddingText] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const subido = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await listFragments(patient.id);
      setFragments(data.fragments);
      setExpiresHours(data.expires_hours);
    } catch (e) {
      toast.error(errMsg(e, "No se pudieron cargar las notas de la sesión"));
    } finally {
      setLoading(false);
    }
  }, [patient.id]);

  useEffect(() => { load(); }, [load]);

  const uploadVoice = useCallback(async (audio) => {
    setUploading(true);
    setUploadFailed(false);
    try {
      const f = await addVoiceFragment(patient.id, audio);
      setFragments((prev) => [...prev, f]);
      reset();
      toast.success("Nota guardada · el audio se ha eliminado");
    } catch (e) {
      setUploadFailed(true);
      toast.error(errMsg(e, "No se pudo guardar la nota de voz"));
    } finally {
      setUploading(false);
    }
  }, [patient.id, reset]);

  // Al detener la grabación, la nota se transcribe y se guarda sola: no hay que pulsar nada más.
  useEffect(() => {
    if (blob && !recording && subido.current !== blob) {
      subido.current = blob;
      uploadVoice(blob);
    }
  }, [blob, recording, uploadVoice]);

  const handleStart = async () => {
    try {
      await start();
    } catch {
      toast.error("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
    }
  };

  const discardVoice = () => {
    reset();
    setUploadFailed(false);
  };

  const handleAddText = async () => {
    if (!text.trim()) return;
    setAddingText(true);
    try {
      const f = await addTextFragment(patient.id, text.trim());
      setFragments((prev) => [...prev, f]);
      setText("");
      toast.success("Nota guardada");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo guardar la nota"));
    } finally {
      setAddingText(false);
    }
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    setSavingEdit(true);
    try {
      const f = await updateFragment(editingId, editText.trim());
      setFragments((prev) => prev.map((x) => (x.id === f.id ? f : x)));
      setEditingId(null);
      toast.success("Nota corregida");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo corregir la nota"));
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (f, n) => {
    if (!window.confirm(`¿Borrar la nota ${n}? No se puede recuperar.`)) return;
    try {
      await deleteFragment(f.id);
      setFragments((prev) => prev.filter((x) => x.id !== f.id));
      toast.success("Nota borrada");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo borrar la nota"));
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const data = await finalizeSession(patient.id);
      onFinalized({ ...data, appointment_id: appointmentId });
    } catch (e) {
      toast.error(errMsg(e, "No se pudo generar el resumen"));
      setFinalizing(false);
    }
  };

  const busy = recording || uploading || finalizing || addingText || savingEdit;
  const total = fragments.length;

  return (
    <div className="max-w-xl mx-auto px-4 pt-4 pb-24">
      <Button variant="ghost" data-testid="session-back-button" onClick={onBack} disabled={recording || uploading || finalizing} className="rounded-full text-slate-500 mb-4">
        <ArrowLeft className="w-4 h-4" /> Volver
      </Button>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">Sesión en curso</p>
          <h2 className="text-2xl font-semibold text-brand-navy mt-1">{patient.name}</h2>
          {patient.reason && <p className="text-slate-500 text-sm mt-1">{patient.reason}</p>}
          <Badge data-testid="session-count-badge" className="mt-3 bg-slate-100 text-slate-600 hover:bg-slate-100 border-0">
            {total === 0 ? "Aún sin notas" : `${total} nota${total === 1 ? "" : "s"} guardada${total === 1 ? "" : "s"}`}
          </Badge>
        </div>

        <div className="py-8 text-center">
          <div className={`mx-auto w-24 h-24 rounded-full flex items-center justify-center ${recording ? "bg-brand-teal rec-pulse" : "bg-brand-navy"}`}>
            {uploading ? <Loader2 className="w-10 h-10 text-white animate-spin" /> : <Mic className="w-10 h-10 text-white" />}
          </div>
          <p className="mt-4 text-3xl font-semibold text-brand-navy tabular-nums" aria-live="polite" data-testid="session-timer">{fmt(seconds)}</p>
          <p className="text-slate-400 text-sm mt-1" aria-live="polite">
            {recording ? "Grabando…"
              : uploading ? "Transcribiendo y guardando…"
              : uploadFailed ? "No se ha podido guardar. Reintenta o descarta la grabación."
              : total === 0 ? "Graba la primera nota de la sesión"
              : "Puedes añadir otra nota cuando quieras"}
          </p>

          <div className="mt-6 flex flex-col gap-3">
            {!recording && !blob && (
              <Button data-testid="session-record-button" onClick={handleStart} disabled={busy} className="rounded-full bg-brand-teal hover:bg-brand-teal/90 h-12">
                <Mic className="w-5 h-5" /> {total === 0 ? "Grabar nota" : "Grabar otra nota"}
              </Button>
            )}
            {recording && (
              <Button data-testid="session-stop-button" onClick={stop} className="rounded-full bg-brand-navy hover:bg-brand-navy/90 h-12">
                <StopCircle className="w-5 h-5" /> Detener y guardar
              </Button>
            )}
            {blob && !recording && uploadFailed && (
              <div className="flex gap-3">
                <Button data-testid="session-retry-button" onClick={() => uploadVoice(blob)} disabled={uploading} className="rounded-full bg-brand-navy hover:bg-brand-navy/90 h-11 flex-1">
                  <RotateCcw className="w-4 h-4" /> Reintentar
                </Button>
                <Button variant="outline" data-testid="session-discard-button" onClick={discardVoice} disabled={uploading} className="rounded-full border-slate-300 text-slate-600 h-11 flex-1">
                  <Trash2 className="w-4 h-4" /> Descartar
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-5">
          <label htmlFor="session-text" className="flex items-center gap-1.5 text-sm font-medium text-brand-navy mb-2">
            <Type className="w-4 h-4" /> O escribe una nota
          </label>
          <Textarea
            id="session-text"
            data-testid="session-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Por ejemplo: ha aguantado 3 series de 10 sin dolor"
            className="min-h-[80px] text-sm"
            disabled={finalizing}
          />
          <Button
            data-testid="session-add-text-button"
            onClick={handleAddText}
            disabled={!text.trim() || busy}
            variant="outline"
            className="mt-2 rounded-full border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white h-10 w-full"
          >
            {addingText ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Añadir nota escrita
          </Button>
        </div>
      </div>

      <section className="mt-6" aria-labelledby="session-notes-title">
        <h3 id="session-notes-title" className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Notas de esta sesión</h3>
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-brand-teal mx-auto" /></div>
        ) : total === 0 ? (
          <p className="text-slate-500 text-sm py-4 text-center" data-testid="session-empty">Todavía no hay notas. Cada nota se guarda en cuanto la grabas o la escribes.</p>
        ) : (
          <ol className="space-y-3" data-testid="session-fragments-list">
            {fragments.map((f, i) => (
              <li key={f.id} data-testid={`session-fragment-${f.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-brand-navy">
                    Nota {i + 1} · {hora(f.created_at)} · {f.source === "voz" ? "Voz" : "Escrita"}
                    {f.edited_at ? " · corregida" : ""}
                  </p>
                  {editingId !== f.id && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" aria-label={`Corregir la nota ${i + 1}`} data-testid={`session-edit-${f.id}`} disabled={busy || finalizing} onClick={() => { setEditingId(f.id); setEditText(f.text); }} className="h-8 text-slate-500">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" aria-label={`Borrar la nota ${i + 1}`} data-testid={`session-delete-${f.id}`} disabled={busy || finalizing} onClick={() => remove(f, i + 1)} className="h-8 text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                {editingId === f.id ? (
                  <div className="mt-2">
                    <Textarea data-testid={`session-edit-input-${f.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} className="min-h-[90px] text-sm" />
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" data-testid={`session-edit-save-${f.id}`} onClick={saveEdit} disabled={!editText.trim() || savingEdit} className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white">
                        {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} disabled={savingEdit} className="rounded-full border-slate-300">
                        <X className="w-4 h-4" /> Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1.5 text-sm text-slate-700 whitespace-pre-wrap">{f.text}</p>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <div className="mt-6">
        <Button
          data-testid="session-finalize-button"
          onClick={handleFinalize}
          disabled={total === 0 || busy || editingId !== null}
          className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white h-12 w-full"
        >
          {finalizing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
          {finalizing ? "Generando el resumen…" : "Finalizar y generar resumen"}
        </Button>
        <div className="mt-4 space-y-1.5 text-xs text-slate-400">
          <p className="flex items-center gap-1.5"><Lock className="w-3 h-3 shrink-0" /> Las notas se guardan cifradas y se borran al guardar el resumen final.</p>
          {expiresHours > 0 && (
            <p className="flex items-center gap-1.5"><Trash2 className="w-3 h-3 shrink-0" /> Si no finalizas la sesión, se borran solas a las {expiresHours} horas.</p>
          )}
          <p className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 shrink-0" /> El audio se elimina en cuanto se transcribe.</p>
        </div>
      </div>
    </div>
  );
};
